import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';

export type AccessMode = 'free' | 'restricted';

const INITIAL_URL = window.location.pathname;

@Injectable({
  providedIn: 'root',
})
export class AppAccessService {
  private readonly ACCESS_MODE_KEY = 'app_access_mode';
  private readonly ENTRY_POINT_KEY = 'app_entry_point';
  private readonly RESTRICTED_ALLOWED_ROUTES = ['/form-builder', '/404', '/privacy-policy'];
  private accessMode: AccessMode | null = null;

  constructor(private router: Router) {
    this.initializeAccessMode();
  }

  private initializeAccessMode(): void {
    // Load mode from sessionStorage if it exists
    const storedMode = sessionStorage.getItem(this.ACCESS_MODE_KEY) as AccessMode | null;
    const storedEntryPoint = sessionStorage.getItem(this.ENTRY_POINT_KEY);

    if (storedMode && storedEntryPoint) {
      this.accessMode = storedMode;
    } else {
      // First time in the session - use the captured URL
      // Save entry point (only the first time)
      sessionStorage.setItem(this.ENTRY_POINT_KEY, INITIAL_URL);

      // Determine mode based on the original entry point
      this.setAccessModeFromRoute(INITIAL_URL);
    }
  }

  private setAccessModeFromRoute(url: string): void {
    if (url.startsWith('/form-builder')) {
      this.setAccessMode('restricted');
    } else if (url.startsWith('/login') || url === '/' || url === '') {
      this.setAccessMode('free');
    }
    // For other routes, keep the current mode or default to 'free'
    else if (!this.getStoredAccessMode()) {
      this.setAccessMode('free');
    }
  }

  private setAccessMode(mode: AccessMode): void {
    this.accessMode = mode;
    sessionStorage.setItem(this.ACCESS_MODE_KEY, mode);
  }

  private getStoredAccessMode(): AccessMode | null {
    // Prioritize in-memory mode over sessionStorage
    return this.accessMode || (sessionStorage.getItem(this.ACCESS_MODE_KEY) as AccessMode | null);
  }

  /**
   * Checks if the user can access a specific route
   */
  canAccessRoute(route: string): boolean {
    const accessMode = this.getStoredAccessMode() || 'free';

    // In free mode, any route can be accessed
    if (accessMode === 'free') {
      return true;
    }

    // In restricted mode, only allowed routes can be accessed
    return this.RESTRICTED_ALLOWED_ROUTES.some((allowedRoute) => route.startsWith(allowedRoute));
  }

  /**
   * Indicates if the application is in restricted mode
   */
  isRestrictedMode(): boolean {
    return this.getStoredAccessMode() === 'restricted';
  }

  /**
   * Gets the current access mode
   */
  getAccessMode(): AccessMode {
    return this.getStoredAccessMode() || 'free';
  }

  /**
   * Clears the access state (useful for testing or logout)
   */
  clearAccessMode(): void {
    sessionStorage.removeItem(this.ACCESS_MODE_KEY);
  }
}
